import React, { useState } from 'react';
import { theme } from '../styles/theme';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { Button } from '../components/Button';
import { Card } from '../components/Card';
import { Input } from '../components/Input';
import { Textarea } from '../components/Textarea';
import { FileUpload } from '../components/FileUpload';
import { Select } from '../components/Select';

export const ReaderProfileEditPage = () => {
  const [formData, setFormData] = useState({
    profilePhoto: null as File | null,
    displayName: 'Sarah Mitchell',
    bio: 'Avid historical fiction reader with a background in archival studies. I love providing detailed feedback on pacing, character development, and historical accuracy.',
    preferredGenres: ['Historical Fiction', 'Mystery', 'Literary Fiction'],
    readingSpeed: 'moderate',
    availabilityStatus: 'available',
    timeCommitment: '2-4-weeks',
    manuscriptsPerMonth: '2',
    contentPreferences: {
      violence: 'any',
      sexualContent: 'mild',
      profanity: 'any',
    },
  });

  const [isSaving, setIsSaving] = useState(false);

  const allGenres = [
    'Literary Fiction',
    'Historical Fiction',
    'Mystery',
    'Thriller',
    'Romance',
    'Fantasy',
    'Science Fiction',
    'Horror',
    'Young Adult',
    'Memoir',
    'Women\'s Fiction',
    'Contemporary Fiction',
  ];

  const readingSpeedOptions = [
    { value: 'slow', label: 'Slow (1-2 chapters/day)' },
    { value: 'moderate', label: 'Moderate (3-5 chapters/day)' },
    { value: 'fast', label: 'Fast (5+ chapters/day)' },
  ];

  const timeCommitmentOptions = [
    { value: '2-4-weeks', label: '2-4 weeks per manuscript' },
    { value: '4-6-weeks', label: '4-6 weeks per manuscript' },
    { value: '6-8-weeks', label: '6-8 weeks per manuscript' },
    { value: 'flexible', label: 'Flexible timeline' },
  ];

  const manuscriptsPerMonthOptions = [
    { value: '1', label: '1 manuscript at a time' },
    { value: '2', label: '2 manuscripts at a time' },
    { value: '3', label: '3+ manuscripts at a time' },
  ];

  const contentPreferenceOptions = [
    { value: 'any', label: 'Any level' },
    { value: 'mild', label: 'Mild only' },
    { value: 'none', label: 'Prefer none' },
  ];

  const handleGenreToggle = (genre: string) => {
    const currentGenres = formData.preferredGenres;
    if (currentGenres.includes(genre)) {
      setFormData({
        ...formData,
        preferredGenres: currentGenres.filter(g => g !== genre),
      });
    } else if (currentGenres.length < 5) {
      setFormData({
        ...formData,
        preferredGenres: [...currentGenres, genre],
      });
    } else {
      window.alert('You can select up to 5 genres');
    }
  };

  const handleSave = () => {
    setIsSaving(true);
    console.log('Saving profile:', formData);

    // Simulate API call
    setTimeout(() => {
      setIsSaving(false);
      window.alert('Profile updated successfully!');
      window.location.href = '/reader/dashboard';
    }, 1000);
  };

  const handleCancel = () => {
    if (window.confirm('Discard changes and return to dashboard?')) {
      window.location.href = '/reader/dashboard';
    }
  };

  return (
    <div style={{
      minHeight: '100vh',
      backgroundColor: theme.colors.background,
      fontFamily: theme.fonts.primary,
    }}>
      <Header />

      <main style={{
        maxWidth: '900px',
        margin: '0 auto',
        padding: '60px 40px',
      }}>
        {/* Page Header */}
        <div style={{
          marginBottom: '40px',
        }}>
          <h1 style={{
            fontSize: '40px',
            fontWeight: '400',
            marginBottom: '8px',
            color: theme.colors.textPrimary,
          }}>
            Edit Profile
          </h1>
          <p style={{
            fontSize: '18px',
            color: theme.colors.textSecondary,
          }}>
            Manage your beta reader profile
          </p>
        </div>

        {/* Profile Basics */}
        <Card style={{ marginBottom: '24px' }}>
          <h2 style={{
            fontSize: '24px',
            fontWeight: '500',
            marginBottom: '24px',
            color: theme.colors.textPrimary,
          }}>
            Profile Basics
          </h2>

          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '24px',
          }}>
            <FileUpload
              label="Profile Photo"
              onChange={(file) => setFormData({ ...formData, profilePhoto: file })}
              helperText="Your profile photo helps writers recognize you"
            />

            <Input
              label="Display Name"
              type="text"
              placeholder="Your name"
              value={formData.displayName}
              onChange={(e) => setFormData({ ...formData, displayName: e.target.value })}
              required
              helperText="How you'd like to be known on the platform"
            />

            <Textarea
              label="Bio"
              placeholder="Tell writers about yourself and your reading background..."
              value={formData.bio}
              onChange={(e) => setFormData({ ...formData, bio: e.target.value })}
              maxLength={300}
              rows={6}
              helperText="Brief introduction about yourself as a reader (300 characters max)"
            />
          </div>
        </Card>

        {/* Reading Preferences */}
        <Card style={{ marginBottom: '24px' }}>
          <h2 style={{
            fontSize: '24px',
            fontWeight: '500',
            marginBottom: '24px',
            color: theme.colors.textPrimary,
          }}>
            Reading Preferences
          </h2>

          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '32px',
          }}>
            {/* Preferred Genres */}
            <div>
              <h3 style={{
                fontSize: '18px',
                fontWeight: '500',
                marginBottom: '12px',
                color: theme.colors.textPrimary,
              }}>
                Preferred Genres (Select up to 5)
              </h3>
              <p style={{
                fontSize: '15px',
                color: theme.colors.textSecondary,
                marginBottom: '16px',
              }}>
                Choose the genres you enjoy reading and providing feedback on
              </p>
              <div style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: '12px',
              }}>
                {allGenres.map((genre) => {
                  const isSelected = formData.preferredGenres.includes(genre);
                  return (
                    <button
                      key={genre}
                      onClick={() => handleGenreToggle(genre)}
                      style={{
                        padding: '12px 20px',
                        fontSize: '15px',
                        fontFamily: theme.fonts.primary,
                        border: `2px solid ${isSelected ? theme.colors.primary : theme.colors.border}`,
                        borderRadius: theme.borderRadius.medium,
                        backgroundColor: isSelected ? theme.colors.primaryLight : theme.colors.white,
                        color: isSelected ? theme.colors.primary : theme.colors.textPrimary,
                        cursor: 'pointer',
                        transition: 'all 0.2s',
                      }}
                    >
                      {isSelected && '✓ '}{genre}
                    </button>
                  );
                })}
              </div>
              <div style={{
                fontSize: '13px',
                color: theme.colors.textTertiary,
                marginTop: '8px',
              }}>
                {formData.preferredGenres.length} of 5 selected
              </div>
            </div>

            <div style={{
              height: '1px',
              backgroundColor: theme.colors.border,
            }}></div>

            {/* Reading Speed */}
            <Select
              label="Reading Speed"
              value={formData.readingSpeed}
              onChange={(e) => setFormData({ ...formData, readingSpeed: e.target.value })}
              options={readingSpeedOptions}
              placeholder="Select your typical reading pace"
              helperText="This helps writers estimate when they'll receive feedback"
            />

            <div style={{
              height: '1px',
              backgroundColor: theme.colors.border,
            }}></div>

            {/* Content Preferences */}
            <div>
              <h3 style={{
                fontSize: '18px',
                fontWeight: '500',
                marginBottom: '12px',
                color: theme.colors.textPrimary,
              }}>
                Content Preferences
              </h3>
              <p style={{
                fontSize: '15px',
                color: theme.colors.textSecondary,
                marginBottom: '20px',
              }}>
                Indicate your comfort level with different types of content
              </p>

              <div style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '16px',
              }}>
                <div>
                  <label style={{
                    display: 'block',
                    fontSize: '15px',
                    fontWeight: '500',
                    marginBottom: '8px',
                    color: theme.colors.textPrimary,
                  }}>
                    Violence
                  </label>
                  <select
                    value={formData.contentPreferences.violence}
                    onChange={(e) => setFormData({
                      ...formData,
                      contentPreferences: {
                        ...formData.contentPreferences,
                        violence: e.target.value,
                      }
                    })}
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      fontSize: '16px',
                      border: `2px solid ${theme.colors.border}`,
                      borderRadius: theme.borderRadius.medium,
                      fontFamily: theme.fonts.primary,
                      color: theme.colors.textPrimary,
                      backgroundColor: theme.colors.white,
                      cursor: 'pointer',
                    }}
                  >
                    {contentPreferenceOptions.map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label style={{
                    display: 'block',
                    fontSize: '15px',
                    fontWeight: '500',
                    marginBottom: '8px',
                    color: theme.colors.textPrimary,
                  }}>
                    Sexual Content
                  </label>
                  <select
                    value={formData.contentPreferences.sexualContent}
                    onChange={(e) => setFormData({
                      ...formData,
                      contentPreferences: {
                        ...formData.contentPreferences,
                        sexualContent: e.target.value,
                      }
                    })}
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      fontSize: '16px',
                      border: `2px solid ${theme.colors.border}`,
                      borderRadius: theme.borderRadius.medium,
                      fontFamily: theme.fonts.primary,
                      color: theme.colors.textPrimary,
                      backgroundColor: theme.colors.white,
                      cursor: 'pointer',
                    }}
                  >
                    {contentPreferenceOptions.map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label style={{
                    display: 'block',
                    fontSize: '15px',
                    fontWeight: '500',
                    marginBottom: '8px',
                    color: theme.colors.textPrimary,
                  }}>
                    Profanity
                  </label>
                  <select
                    value={formData.contentPreferences.profanity}
                    onChange={(e) => setFormData({
                      ...formData,
                      contentPreferences: {
                        ...formData.contentPreferences,
                        profanity: e.target.value,
                      }
                    })}
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      fontSize: '16px',
                      border: `2px solid ${theme.colors.border}`,
                      borderRadius: theme.borderRadius.medium,
                      fontFamily: theme.fonts.primary,
                      color: theme.colors.textPrimary,
                      backgroundColor: theme.colors.white,
                      cursor: 'pointer',
                    }}
                  >
                    {contentPreferenceOptions.map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          </div>
        </Card>

        {/* Availability & Commitments */}
        <Card style={{ marginBottom: '24px' }}>
          <h2 style={{
            fontSize: '24px',
            fontWeight: '500',
            marginBottom: '24px',
            color: theme.colors.textPrimary,
          }}>
            Availability & Commitments
          </h2>

          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '24px',
          }}>
            <div>
              <label style={{
                display: 'block',
                fontSize: '16px',
                fontWeight: '500',
                marginBottom: '12px',
                color: theme.colors.textPrimary,
              }}>
                Availability Status
              </label>
              <div style={{
                display: 'flex',
                gap: '12px',
                flexWrap: 'wrap',
              }}>
                {[
                  { value: 'available', label: 'Available Now', color: theme.colors.success },
                  { value: 'limited', label: 'Limited Availability', color: theme.colors.warning },
                  { value: 'not-available', label: 'Not Available', color: theme.colors.error },
                ].map((option) => (
                  <button
                    key={option.value}
                    onClick={() => setFormData({ ...formData, availabilityStatus: option.value })}
                    style={{
                      padding: '16px 24px',
                      fontSize: '16px',
                      fontFamily: theme.fonts.primary,
                      border: `2px solid ${formData.availabilityStatus === option.value ? option.color : theme.colors.border}`,
                      borderRadius: theme.borderRadius.medium,
                      backgroundColor: formData.availabilityStatus === option.value ? `${option.color}20` : theme.colors.white,
                      color: formData.availabilityStatus === option.value ? option.color : theme.colors.textPrimary,
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                      flex: '1',
                      minWidth: '150px',
                    }}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
              <div style={{
                fontSize: '14px',
                color: theme.colors.textSecondary,
                marginTop: '8px',
              }}>
                Writers can see your availability status when browsing beta readers
              </div>
            </div>

            <Select
              label="Time Commitment"
              value={formData.timeCommitment}
              onChange={(e) => setFormData({ ...formData, timeCommitment: e.target.value })}
              options={timeCommitmentOptions}
              placeholder="How long do you typically need?"
              helperText="Average time to read and provide feedback"
            />

            <Select
              label="Manuscripts Per Month"
              value={formData.manuscriptsPerMonth}
              onChange={(e) => setFormData({ ...formData, manuscriptsPerMonth: e.target.value })}
              options={manuscriptsPerMonthOptions}
              placeholder="How many can you handle?"
              helperText="How many manuscripts you can beta read simultaneously"
            />
          </div>
        </Card>

        {/* Action Buttons */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          gap: '16px',
        }}>
          <Button variant="ghost" onClick={handleCancel}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={isSaving}>
            {isSaving ? 'Saving...' : 'Save Changes'}
          </Button>
        </div>

        {/* Help Text */}
        <Card style={{
          marginTop: '24px',
          backgroundColor: theme.colors.backgroundAlt,
        }}>
          <p style={{
            fontSize: '15px',
            lineHeight: '1.6',
            color: theme.colors.textSecondary,
          }}>
            <strong style={{ color: theme.colors.textPrimary }}>Privacy Note:</strong> Your profile information helps writers find the right beta readers for their manuscripts. You can update your availability status anytime to control whether you appear in search results.
          </p>
        </Card>
      </main>

      <Footer />
    </div>
  );
};
